// --- ADDED: A configurable list of modes that are allowed to generate a provocation. ---
// This "allow-list" is safer than a "deny-list." New modes won't get a provocation
// unless you explicitly add their name here.
const PROVOCATION_ENABLED_MODES = [
'Strategist',
'Adaptive Strategist', 
'Innovation Catalyst', 
'Reality Check',
'Strategic Alternatives',
'Market Shake-Up',
'Risk Horizon Scan',
];

// --- ADDED: A new main "controller" function to act as the gatekeeper. ---
// Your application should now call THIS function instead of the others directly.
export function runProvocationEngine(userInput, activeMode) {
  // THE GATE: Check if the current activeMode is on the approved list.
  if (!PROVOCATION_ENABLED_MODES.includes(activeMode)) {
    // If not, stop immediately and return null.
    console.log(`provocationEngine.js: Gate triggered. Skipping provocation for mode: ${activeMode}`);
    return null;
  }

  // If the gate passes, proceed with the normal logic.
  console.log(`provocationEngine.js: Gate passed. Running provocation for mode: ${activeMode}`);
  const intentResult = inferUserIntent(userInput);
  const provocationQuestion = getProvocation(intentResult.primaryIntent);

  return {
    ...intentResult, // Return the full intent object
    provocationQuestion, // And add the question to it
  };
}

// --- NOTE: The functions below are mostly unchanged, but are now called by the controller. ---

export function inferUserIntent(message) {
  const msg = message.toLowerCase();
  console.log("🧠 Raw user input:", message);
  console.log("🧠 Normalized input:", msg);

  const patterns = {
    exploration: [
      /curious/, /explore/, /not.*sure/, /figure.*out/, /possibilities/,
      /just.*looking/, /discovery/, /see.*what.*possible/, /not.*decided/,
      /thinking.*through/, /hypothetical/, /vision/, /long[- ]?term/,
      /unclear/, /open[- ]?ended/, /speculate/, /brainstorm/
    ],
    ambition: [
      /10x/, /moonshot/, /bold/, /crazy/, /disrupt/, /paradigm/,
      /game.*changing/, /radical/, /category.*defining/,
      /world.*changing/, /breakthrough/, /trailblazing/, /visionary/
    ],
    efficiency: [
      /no.*budget/, /limited/, /scrappy/, /bootstrap/, /lean/,
      /minimum.*resources/, /hack/, /cheap/, /fast/, /frugal/,
      /optimize/, /quick.*win/, /barebones/
    ],
    regulation: [
      /legal/, /regulation/, /compliance/, /approval/, /licensed/,
      /pharma/, /healthcare/, /HIPAA/, /SEC/, /policy/, /certification/,
      /audit/, /GDPR/, /FDA/
    ],
    copycat_risk: [
      /competition/, /clone/, /copy/, /similar/, /same.*as/, /imitate/,
      /replica/, /saturated/, /too.*common/, /duplicated/, /copied/,
      /competitive.*edge/, /unique/
    ],
    execution: [
      /mvp/, /launch/, /prototype/, /build/, /develop/, /shipping/,
      /deadline/, /timeline/, /roll.*out/, /release/, /feature.*set/,
      /iteration/, /test.*version/
    ],
    market_fit: [
      /traction/, /pmf/, /product.*market.*fit/, /churn/, /retention/,
      /conversion/, /usage/, /engagement/, /fit/, /users.*sticking/,
      /active.*users/, /repeat/, /metrics/
    ],
    scaling: [
      /scale/, /scale.*up/, /grow.*fast/, /expansion/, /10k.*users/,
      /infrastructure/, /server.*load/, /handle.*demand/, /bandwidth/,
      /throughput/, /growth/, /ops/, /devops/
    ]
  };
  const scores = {};
  const matchedKeywords = {};

  for (const intent in patterns) {
    const matches = patterns[intent].filter((pattern) => pattern.test(msg));
    if (matches.length > 0) {
      scores[intent] = matches.length;
      matchedKeywords[intent] = matches.map((m) => m.source);
    }
  }

  if (Object.keys(scores).length === 0) {
    console.log("🔍 Matched intent: default");
    return {
      primaryIntent: "default",
      secondaryIntents: [],
      matchedKeywords: {},
      summary: "No specific innovation theme detected — defaulting to general insight."
    };
  }

  console.log("🔍 Intent scores:", scores);
  console.log("🧠 Matched keywords:", matchedKeywords);

  const maxScore = Math.max(...Object.values(scores));
  const tiedIntents = Object.entries(scores)
    .filter(([_, score]) => score === maxScore)
    .map(([intent]) => intent);

  const priority = [
    "ambition",
    "exploration",
"execution",
    "market_fit",
    "scaling",
    "copycat_risk",
    "efficiency",
    "regulation"
  ];

  const primaryIntent = tiedIntents.length === 1
    ? tiedIntents[0]
    : priority.find((intent) => tiedIntents.includes(intent));

  const secondaryIntents = Object.keys(scores)
    .filter((intent) => intent !== primaryIntent)
    .sort((a, b) => scores[b] - scores[a]);

  const summary = `Primary focus: ${primaryIntent}.`
    + (secondaryIntents.length
      ? ` Also detected: ${secondaryIntents.slice(0, 2).join(", ")}.`
      : "");

  console.log(`✅ Top matched intent: ${primaryIntent}`);
  console.log(`🧩 Secondary intents: ${secondaryIntents}`);
  console.log(`📌 Summary: ${summary}`);

  return {
    primaryIntent,
    secondaryIntents,
    matchedKeywords,
    summary
  };
}

export function getProvocation(userIntent) {
  const descriptors = {
    ambition: "Push boundaries, bold bets, investor perception",
    efficiency: "Lean ops, resource constraints, shortcuts with trade-offs",
    regulation: "Compliance, legal hurdles, sudden policy risk",
    copycat_risk: "Moats, defensibility, competitor retaliation",
    execution: "Deadlines, launch speed, cracks in delivery",
    market_fit: "Stickiness, churn, user behavior reality check",
    scaling: "Bottlenecks, system fragility, cost explosions",
    exploration: "Long-term vision, curiosity, speculative upside",
    default: "General critical assumption or blind spot"
  };
  return descriptors[userIntent] || descriptors.default;
}